import React from "react";
import {isIOS, isAndroid} from "react-device-detect";
import {
    getTranslate,
    Translate,
    TranslateFunction,
} from "react-localize-redux";
import {connect} from "react-redux";
import {
    Button,
    Dropdown,
    Menu,
    Modal,
    Tooltip,
} from "antd";
import Nullable from "@data-force/app/interfaces/nullable";
import {DownOutlined, ExclamationCircleOutlined, UnorderedListOutlined} from "@ant-design/icons";
import classNames from "classnames";
import dayjs, {Dayjs} from "dayjs";
import duration from "dayjs/plugin/duration";
import relativeTime from "dayjs/plugin/relativeTime";
import ReactMarkdown from "react-markdown";
import {history} from "@data-force/app";
import {
    ClaimJobPhase, CompleteJobPhase, JobEntity, OpenExternalJobData, PhaseConfig, PhaseConfigNormalized,
} from "../../interfaces/store/actions/payload/job";
import {
    WorkflowDefinition,
    WorkflowDefinitionPhases,
} from "../../interfaces/store/actions/payload/workflowDefinition";
import {
    ClaimJobPhaseAction,
    CompleteJobPhaseAction,
    GetJobPhaseWorkflowDefinitionsAction,
    OpenExternalJobAction,
    SetActiveJobTypeAction,
    SetFilterTypeAction,
    SetSelectedJobAction,
    ShowConfirmClaimDialogAction,
    ShowDetailsDrawerAction,
    ToggleInitiateWorkDialogAction,
} from "../../interfaces/store/actions/job";
import {
    isExternalProject,
    formatString,
    getFormattedTime,
    getLocalePrefix,
    getOpeningUrl,
    getHistoryParams,
} from "../../utils/common";
import {PaymentStats, TaskPhaseStats, TaskStats} from "../../interfaces/store/actions/payload/task";
import {
    ContentType, ActivityNotification, ProjectType, FilterType,
} from "../../enums/enums";
import StoreState from "../../interfaces/store/storeState";
import {
    claimJobPhase,
    completeJobPhase,
    getJobPhaseWorkflowDefinitions,
    openExternalJob,
    setActiveJobType,
    setFilterType,
    setSelectedJob,
    showConfirmClaimDialog,
    showDetailsDrawer,
    toggleInitiateWorkDialog,
} from "../../store/actions/job";
import "./CardItem.scss";
import {Editors} from "../../interfaces/common";
import MenuClickParam from "../../interfaces/MenuClickParam";
import {DrawerTabs} from "../../utils/constants";
import ProgressBar from "../ProgressBar/ProgressBar";
import EditorIcon from "../EditorIcon/EditorIcon";
import JobStatus from "../JobStatus/JobStatus";
import HistoryParams from "../../interfaces/historyParams";
import TestScore from "../TestScore/TestScore";
import CardInfo from "../CardInfo/CardInfo";
import {ReadyTasksIcon} from "../../assets/Icons";
import {TabManager} from "../../utils/TabManager";
import JobTitle from "../common/JobTitle/JobTitle";

const {confirm, info}: typeof Modal = Modal;

type StatsData = {
    completedTasks: number
    rejectedTasks: number
    numOfTasks: number
    readyTasks: number
    approvedForPaymentTasks: number
};

dayjs.extend(duration);
dayjs.extend(relativeTime);

interface OwnProps {
    data: JobEntity
    hideBtnDropdown: boolean
}

interface StateProps {
    editors: Editors
    phaseConfigList: PhaseConfigNormalized
    workflowDefinitionList: WorkflowDefinition[]
    translate: TranslateFunction
    isMobile: boolean
    activeJobType: string
    activeTestType: string
    playStoreUrl: string
    appStoreUrl: string
    isTest: Nullable<boolean>
}

interface DispatchProps {
    claimJobPhase(payload: ClaimJobPhase): ClaimJobPhaseAction

    completeJobPhase(payload: CompleteJobPhase): CompleteJobPhaseAction

    showConfirmClaimDialog(payload: JobEntity): ShowConfirmClaimDialogAction

    getJobPhaseWorkflowDefinitions(): GetJobPhaseWorkflowDefinitionsAction

    setSelectedJob(payload: Nullable<JobEntity>): SetSelectedJobAction

    showDetailsDrawer(payload: string): ShowDetailsDrawerAction

    openExternalJob(payload: OpenExternalJobData): OpenExternalJobAction

    toggleInitiateWorkDialog(payload: Nullable<JobEntity>): ToggleInitiateWorkDialogAction

    setFilterType(payload: string): SetFilterTypeAction

    setActiveJobType(payload: string): SetActiveJobTypeAction
}

type Props = OwnProps & StateProps & DispatchProps;

export class CardItem extends React.Component<Props> {
    private static readonly translationPath: string = "components.CardItem.CardItem";

    private static readonly translationConfirm: string = "components.CardItem.CardItem.confirmDialog";

    private static readonly translateMobileWarning: string = "components.CardItem.CardItem.mobileWarningDialog";

    private static readonly translateRecordingPhaseDialog: string = "components.CardItem.CardItem.recordingPhaseDialog";

    private static readonly translationPathJobActions: string = "enums.JobActions";

    private static readonly translateClaimLimit: string = "components.CardItem.CardItem.claimLimitNoticeDialog";

    private percentageFormat: string = this.props.translate(`${CardItem.translationPath}.percentageFormat.text`) as string;

    private jobCodeNameFormat: string = this.props.translate(`${CardItem.translationPath}.jobCodeNameFormat.text`) as string;

    constructor(props: Props) {
        super(props);
        this.claimPhaseHandler = this.claimPhaseHandler.bind(this);

        this.getRemainingTime = this.getRemainingTime.bind(this);

        this.showClaimLimitNotice = this.showClaimLimitNotice.bind(this);

        this.detailsBtnHandler = this.detailsBtnHandler.bind(this);
    }

    /**
     * Fire complete job phase action with necessary arguments
     * @param {string} event
     * @param {string} notificationType
     */
    onOkHandler(event: string, notificationType: string): void {
        const {
            name: phase,
            projectId,
            jobId,
            title,
        }: JobEntity = this.props.data;
        this.props.completeJobPhase({
            projectId,
            jobId,
            phase,
            event,
            notificationType,
            title,
        });
    }

    /**
     * Generates menu for current task from available actions provided
     *
     * @param {Nullable<string[]>} availableEvents
     */
    getMoreActionsMenu(availableEvents: Nullable<string[]>): React.ReactElement<Menu> {
        const {REOPEN, MOVE_TO}: typeof ActivityNotification = ActivityNotification;

        return (
            <Menu>
                {
                    availableEvents?.map((event: string): React.ReactNode => {
                        let eventKey: string = event;
                        const defaultEventIndex: number = 0;
                        let reopenOrCompleteToPhaseName: string = "";
                        // Reject that reopens and COMPLETE.MOVE_TO has phase name separated by space
                        const phaseNameIndex: number = event.indexOf(" ");
                        if (phaseNameIndex !== -1) {
                            eventKey = event.slice(0, phaseNameIndex);
                            reopenOrCompleteToPhaseName = event.slice(phaseNameIndex);
                        }
                        // Parallel phases and multiple events will have a dot in event name,
                        // we separate them with a dot so that we don't use multiple localizations for each one
                        const splitEventKey: string[] = eventKey.split(".");

                        if (splitEventKey.includes(REOPEN)) {
                            eventKey = REOPEN;
                        } else if (splitEventKey.includes(MOVE_TO)) {
                            eventKey = MOVE_TO;
                        } else {
                            eventKey = splitEventKey[defaultEventIndex];
                        }

                        return (
                            <Menu.Item
                                onClick={this.actionSelectedHandler.bind(this, event, eventKey, reopenOrCompleteToPhaseName)}
                                key={event}
                            >
                                <Translate id={`${CardItem.translationPathJobActions}.${eventKey}.text`}/>
                                {reopenOrCompleteToPhaseName}
                            </Menu.Item>
                        );
                    })
                }
            </Menu>
        );
    }

    /**
     * Returns available actions for current job
     *
     * @return {string[]}
     */
    getAvailableActions(): string[] {
        let availableEvents: string[] = [];
        const {
            name: phaseName,
            projectType,
            taskCount,
            completedTaskCount,
            phaseStats,
            workflowDefinitionId,
        }: JobEntity = this.props.data;
        if (phaseStats) {
            const {READY = 0, IN_PROGRESS = 0, REOPENED = 0}: TaskPhaseStats = phaseStats;
            const {phaseConfigList, workflowDefinitionList, getJobPhaseWorkflowDefinitions: getWorkflowDefinitions}: Props = this.props;
            const enableBulkCompletePhaseAction: boolean = phaseConfigList[`${projectType}-${phaseName}`]?.enableBulkCompletePhaseAction;

            if (enableBulkCompletePhaseAction && completedTaskCount !== taskCount && (READY || IN_PROGRESS || REOPENED)) {
                const workflowDefinition: Nullable<WorkflowDefinition> = workflowDefinitionList.find(
                    (currentWFD: WorkflowDefinition): boolean => workflowDefinitionId === currentWFD.id,
                );
                if (workflowDefinition) {
                    const phase: Nullable<WorkflowDefinitionPhases> = workflowDefinition.phases.find(
                        (currentPhase: WorkflowDefinitionPhases): boolean => currentPhase.name === phaseName,
                    );
                    if (phase) {
                        availableEvents = this.getPhaseActions(phase);
                    }
                } else if (!workflowDefinitionList.length) {
                    getWorkflowDefinitions();
                }
            }
        }

        return availableEvents;
    }

    /**
     * Returns due date in 'time x left' form or overdue text
     * @param {number} date
     * @return {React.ReactNode}
     */
    getRemainingTime(date: number): React.ReactNode {
        const {translate}: Props = this.props;
        const dateFormat: string = translate(`${CardItem.translationPath}.dateFormat.text`) as string;
        // everything less than 35h is considered as a 'day left' by dayjs, confirmed by the pdm
        const warningHours: number = 35;
        const now = dayjs(new Date());
        const dueDate: Nullable<Dayjs> = dayjs(date)
            .hour(23)
            .minute(59)
            .second(59);
        const hoursLeft: number = dayjs.duration(dueDate.diff(now))
            .asHours();

        let overDueInfo: string = translate(`${CardItem.translationPath}.overdueText.text`) as string;
        if (dueDate.isAfter(now)) {
            overDueInfo = formatString(dateFormat, dueDate.fromNow(true));
        }
        return <span className={warningHours > hoursLeft ? "df-task-warning" : ""}>{overDueInfo}</span>;
    }

    /**
     * Creates list of available actions, for given phase
     *
     * @param {Nullable<WorkflowDefinitionPhases>} phase
     * @return {string[]}
     */
    getPhaseActions(phase: Nullable<WorkflowDefinitionPhases>): string[] {
        let phaseActions: string[] = [];
        if (phase) {
            const parallelPhases: boolean = !!phase.parent;
            if (parallelPhases) {
                const availableEvents: Nullable<string[]> = phase.availableEvents?.filter(
                    (event: string): boolean => event.includes(phase.name),
                );
                phaseActions = availableEvents || phaseActions;
            } else {
                phaseActions = phase.availableEvents || phaseActions;
            }
        }

        return phaseActions;
    }

    /**
     * Returns all necessary stats info for a given job, takes into consideration which folder is currently active
     * @return React.ReactNode
     */
    getRegularStats(): React.ReactNode {
        const {data, translate, activeJobType}: Props = this.props;
        const {translationPath}: typeof CardItem = CardItem;
        const {
            open,
            assignee,
            taskCount,
            estimatedTime,
            timeLimit,
            idx,
            paymentStats,
            phaseStats: {READY = 0, REOPENED = 0},
            userAvailableCount,
        }: JobEntity = data;

        const {
            completedTasks, numOfTasks, approvedForPaymentTasks, rejectedTasks,
        }: StatsData = this.getPhaseStatsData(data);
        const isFirstPhase: boolean = idx === 0; // determine first workflow phase;
        const isNotCompleted: boolean = activeJobType !== ContentType.COMPLETED;

        return (
            <>
                {!!estimatedTime && <CardInfo data={getFormattedTime(estimatedTime, translate)} type="timeEstimate"/>}
                {(!!timeLimit && isNotCompleted) && <CardInfo data={getFormattedTime(timeLimit, translate)} type="timeLimit"/>}
                {!!taskCount && (
                    <div className="df-card-info">
                        {isNotCompleted && !isFirstPhase && (
                            <div className="df-job-ready-stat">
                                <Tooltip title={translate(`${translationPath}.readyTasks.text`)}>
                                    <span role="img">
                                        <ReadyTasksIcon aria-label={translate(`${translationPath}.readyTasks.text`) as string}/>
                                    </span>
                                    <span>{READY + REOPENED}</span>
                                </Tooltip>
                            </div>
                        )}
                        <Tooltip title={translate(`${translationPath}.totalTasks.text`)} className="df-qa-task-count-stat">
                            <UnorderedListOutlined aria-label={translate(`${translationPath}.totalTasks.text`) as string}/>
                            <span>{numOfTasks}</span>
                        </Tooltip>
                    </div>
                )}
                {assignee && !open && (
                    <>
                        <div className="df-task-stats">
                            <CardInfo data={completedTasks} type="completedTasks"/>
                            <CardInfo data={rejectedTasks} type="rejectedTasks"/>
                        </div>
                        {activeJobType === ContentType.COMPLETED && paymentStats && (
                            <CardInfo data={approvedForPaymentTasks} type="approvedForPaymentTasks"/>
                        )}
                    </>
                )}
                {open && <CardInfo data={userAvailableCount} type="availableToClaim"/>}
            </>
        );
    }

    /**
     * Sanitize phaseStats data when there is faulty server side statsData
     * @param {JobEntity} data
     * @return {StatsData}
     */
    getPhaseStatsData(data: JobEntity): StatsData {
        const {
            taskCount = 0, phaseStats, taskStats, paymentStats,
        }: JobEntity = data;
        let completedTasks: number = 0;
        let rejectedTasks: number = 0;
        let approvedForPaymentTasks: number = 0;
        let readyTasks: number = 0;
        if (phaseStats) {
            const {
                COMPLETED = 0, REOPENED = 0, READY = 0,
            }: TaskPhaseStats = phaseStats;
            completedTasks = COMPLETED;
            readyTasks = READY + REOPENED;
        }

        if (taskStats) {
            const {REJECTED = 0}: TaskStats = taskStats;
            rejectedTasks = REJECTED;
        }

        if (paymentStats) {
            const {APPROVED = 0}: PaymentStats = paymentStats;
            approvedForPaymentTasks = APPROVED;
        }

        return {
            completedTasks, rejectedTasks, numOfTasks: taskCount, readyTasks, approvedForPaymentTasks,
        };
    }

    /**
     * Returns all necessary stats info for a given test, takes into consideration which folder is currently active
     * @return React.ReactNode
     */
    getTestStats(): React.ReactNode {
        const {data, activeTestType, translate}: Props = this.props;
        const {
            estimatedTime,
            timeLimit,
            minimumScoreThreshold = 0,
            open,
            idx,
            taskCount,
            phaseStats: {READY = 0, REOPENED = 0},
            userAvailableCount,
        }: JobEntity = data;

        const isFirstPhase: boolean = idx === 0; // determine first workflow phase;
        const isNotCompleted: boolean = activeTestType !== ContentType.COMPLETED;

        return (
            <>
                {activeTestType !== ContentType.COMPLETED && (
                    <>
                        {!!estimatedTime && (<CardInfo data={getFormattedTime(estimatedTime, translate)} type="timeEstimate"/>)}
                        {!!timeLimit && <CardInfo data={getFormattedTime(timeLimit, translate)} type="timeLimit"/>}
                    </>
                )}
                {!!taskCount && isNotCompleted && !isFirstPhase && (
                    <CardInfo data={READY + REOPENED} type="readyTasks"/>
                )}
                {open && <CardInfo data={userAvailableCount} type="attempts"/>}
                {!!minimumScoreThreshold && (
                    <CardInfo
                        data={formatString(this.percentageFormat, minimumScoreThreshold.toString())}
                        type="passingScore"
                    />
                )}
            </>
        );
    }

    /**
     * Executes opening of editors. In case it’s an external project
     *  - if there are multiple tasks we redirect to the drawer list with available tasks
     *  - if there is one task we redirect to opening of the editor
     * In other cases it opens passed url in new tab
     *
     * @param {boolean} isExternal
     * @param {string} url
     */
    confirmOpenHandler(isExternal: boolean, url: string): void {
        const {
            activeJobType, data: {
                projectId, jobId, name, taskCount,
            },
        }: Props = this.props;
        if (isExternal) {
            const {COMPLETED, IN_PROGRESS}: typeof ContentType = ContentType;
            const {ONGOING, DONE}: typeof FilterType = FilterType;
            const completed: boolean = activeJobType === COMPLETED;

            this.props.setFilterType(completed ? DONE : ONGOING);
            this.props.setActiveJobType(completed ? COMPLETED : IN_PROGRESS);
            this.props.showDetailsDrawer(DrawerTabs.STATS);

            const historyParams: Nullable<HistoryParams> = getHistoryParams();
            history.push(`/${historyParams?.idpId}/jobs`);

            if (taskCount === 1) {
                TabManager.getInstance().openTab(null);
                this.props.openExternalJob({projectId, jobId, phaseName: name});
            }
        } else {
            TabManager.getInstance().openTab(url);
        }
    }

    /**
     * Details button handler, shows info dialog regarding dfc jobs
     * @param {React.MouseEvent} e
     */
    detailsBtnHandler(e: React.MouseEvent): void {
        e.stopPropagation();
        const {translateRecordingPhaseDialog}: typeof CardItem = CardItem;
        const {translate, appStoreUrl, playStoreUrl}: Props = this.props;

        let okBtnTxt: string = translate(`${translateRecordingPhaseDialog}.okBtnTxt.text`) as string;
        let content: string = translate(`${translateRecordingPhaseDialog}.content.text`) as string;

        if (isIOS) {
            okBtnTxt = translate(`${translateRecordingPhaseDialog}.iosBtnTxt.text`) as string;
            content = translate(`${translateRecordingPhaseDialog}.iosContent.text`) as string;
        }
        if (isAndroid) {
            okBtnTxt = translate(`${translateRecordingPhaseDialog}.androidBtnTxt.text`) as string;
            content = translate(`${translateRecordingPhaseDialog}.iosContent.text`) as string;
        }

        info({
            className: "df-recording-phase-modal",
            title: translate(`${translateRecordingPhaseDialog}.title.text`),
            content,
            centered: true,
            maskClosable: true,
            closable: true,
            okText: okBtnTxt,
            onOk: (): void => {
                if (isIOS) {
                    setTimeout(window.open, 100, appStoreUrl);
                }
                if (isAndroid) {
                    setTimeout(window.open, 100, playStoreUrl);
                }
            },
            width: 572,
        });
    }

    /**
     * Based on type of selected action show a confirm dialog
     * @param {string} action
     * @param {string} notificationType
     * @param {string} phase
     * @param {MenuClickParam} menuInfo
     */
    actionSelectedHandler(action: string, notificationType: string, phase: string, menuInfo: MenuClickParam): void {
        menuInfo.domEvent.stopPropagation();
        this.showConfirmation(action, notificationType, phase);
    }

    /**
     * Based on event type show confirmation dialog with appropriate text, pass event type to 'onOk' handler
     * @param {string} event
     * @param {string} notificationType
     * @param {string} phase
     */
    showConfirmation(event: string, notificationType: string, phase: string): void {
        const {translationConfirm}: typeof CardItem = CardItem;
        const {translate}: Props = this.props;

        confirm({
            className: "df-complete-confirm",
            title: formatString(translate(`${translationConfirm}.${notificationType}_title.text`) as string, phase),
            content: translate(`${translationConfirm}.content.text`),
            okText: translate(`${translationConfirm}.${notificationType}_btn.text`),
            cancelText: translate(`${translationConfirm}.cancelBtn.text`),
            centered: true,
            onOk: this.onOkHandler.bind(this, event, notificationType),
        });
    }

    /**
     * Shows a warning when a user wants to claim more than the job/test set limit
     */
    showClaimLimitNotice(): void {
        const {translateClaimLimit}: typeof CardItem = CardItem;
        const {translate, data: {name, title}, isTest}: Props = this.props;
        const localePrefix: string = getLocalePrefix(isTest);

        confirm({
            className: "df-dialog-confirm",
            title: translate(`${translateClaimLimit}.${localePrefix}Title.text`),
            content: (
                <>
                    {translate(`${translateClaimLimit}.${localePrefix}ContentPart1.text`)}
                    <JobTitle {...{translate, title, phaseName: name}}/>
                    {translate(`${translateClaimLimit}.${localePrefix}ContentPart2.text`)}
                </>),
            icon: <ExclamationCircleOutlined aria-hidden/>,
            centered: true,
            okCancel: false,
        });
    }

    /**
     * Actions container renderer function
     *
     * @param {JobEntity} data
     * @return {React.ReactNode}
     */
    actionsRenderer(data: JobEntity): React.ReactNode {
        let claimAvailable: boolean = true;
        const {
            started,
            assignee,
            open,
            taskCount,
            completedTaskCount,
            name,
        }: JobEntity = data;
        if (open) {
            const {
                cloneCount = 0,
                cloneLimit = 0,
                userCloneLimit = 0,
                stopped,
                userCloneCount = 0,
            }: JobEntity = data;

            claimAvailable = !stopped && (cloneLimit > cloneCount ? !!(userCloneLimit - userCloneCount) : false);
        }

        const completed: boolean = completedTaskCount === taskCount;

        return started && (!assignee || open) && !completed
            ? this.nonAssignedActionsRenderer(claimAvailable, name) : this.assignedActionsRenderer();
    }

    /**
     * Based on current job phase name, we find matching phase name in phase config items
     * and on that found phase we return whether phase is of type review
     *
     * @return {Nullable<boolean>}
     */
    findReviewTypePhase(): Nullable<boolean> {
        const {phaseConfigList, data: {name}}: Props = this.props;
        const foundPhase: Nullable<PhaseConfig> = Object.values(phaseConfigList)
            .find((phase: PhaseConfig): boolean => phase.name === name);
        return foundPhase?.review;
    }

    /**
     * Handles creation of 'claim' or 'details' button for dfc jobs.
     * In case of test jobs, we use locale prefix for different button labels.
     *
     * @param {boolean} claimAvailable
     * @param {string} phaseName
     * @return {Nullable<React.ReactNode>}
     */
    nonAssignedActionsRenderer(claimAvailable: boolean, phaseName: string): Nullable<React.ReactNode> {
        const {isTest}: Props = this.props;
        let btn: Nullable<React.ReactNode>;
        let localePrefix: string = getLocalePrefix(isTest);
        if (isTest && this.findReviewTypePhase()) {
            localePrefix = "review";
        }

        if (claimAvailable) {
            btn = phaseName === "Recording" ? (
                <Button
                    key="details"
                    type="primary"
                    onClick={this.detailsBtnHandler}
                >
                    <Translate id={`${CardItem.translationPath}.details.text`}/>
                </Button>
            ) : (
                <Button
                    key="claim"
                    type="primary"
                    onClick={this.claimPhaseHandler}
                >
                    <Translate id={`${CardItem.translationPath}.${localePrefix}Claim.text`}/>
                </Button>
            );
        }

        return btn;
    }

    /**
     * Creates actions visible when user is assigned to provided task.
     * This is only applicable when we are NOT in completed folder for tests
     *
     * @return {Nullable<React.ReactNode> }
     */
    assignedActionsRenderer(): Nullable<React.ReactNode> {
        const {
            editors,
            phaseConfigList,
            data,
            hideBtnDropdown,
            activeTestType,
            isTest,
        }: Props = this.props;
        const availableEvents: Nullable<string[]> = this.getAvailableActions();
        const actionsMenu: React.ReactElement<Menu> = this.getMoreActionsMenu(availableEvents);
        const openJobUrl: string = getOpeningUrl(editors, phaseConfigList, data);
        const showDropdown: boolean = !hideBtnDropdown && !!availableEvents.length;
        const openBtnCls: string = classNames({
            "df-open-job": true,
            "df-open-with-dropdown": showDropdown,
        });

        const btnLabel: string = isTest && this.findReviewTypePhase() ? "continue" : "open";

        return isTest && activeTestType === ContentType.COMPLETED ? null : (
            <>
                <Button
                    className={openBtnCls}
                    key="open"
                    disabled={!openJobUrl && !isExternalProject(data.projectType)}
                    onClick={this.openJobHandler.bind(this, openJobUrl)}
                >
                    <Translate id={`${CardItem.translationPath}.${btnLabel}.text`}/>
                </Button>
                {showDropdown && (
                    <Dropdown
                        className="df-more-actions"
                        overlay={actionsMenu}
                        trigger={["click"]}
                        placement="bottom"
                        align={{offset: [-25, 0]}}
                    >
                        <DownOutlined
                            key="more-actions"
                            onClick={this.stopEventPropagation}
                        />
                    </Dropdown>
                )}
            </>
        );
    }

    /**
     * Show dialog for "Claim job phase confirmation" or in case of tests shows initiate work dialog. In case when
     * clone limit is reached for either tests or jobs, we show claim warning dialog.
     * @param {React.MouseEvent} e
     */
    claimPhaseHandler(e: React.MouseEvent): void {
        e.stopPropagation();
        const {data: {open, ongoingAvailableCount, ongoingCloneLimit}, isTest}: Props = this.props;

        if (ongoingCloneLimit && !ongoingAvailableCount && open) {
            this.showClaimLimitNotice();
        } else if (isTest) {
            this.props.toggleInitiateWorkDialog(this.props.data);
        } else {
            this.props.showConfirmClaimDialog(this.props.data);
        }
    }

    /**
     * Open job button handler, in case if is an external job and job has multiple tasks then it opens the JobDrawer,
     * in case if we are under 768px i.e. on mobile then shows warning dialog for confirm opening
     * in case if we have external job with one task then executes getExternalJobUrl action
     * and in other cases it opens the passed url in a new window
     *
     * @param {string} url
     * @param {React.MouseEvent} e
     */
    openJobHandler(url: string, e: React.MouseEvent): void {
        e.stopPropagation();
        const {translate, isMobile, data} = this.props;
        const {projectType}: JobEntity = data;
        const externalProject: boolean = isExternalProject(projectType);
        this.props.setSelectedJob(data);

        if (isMobile) {
            const {translateMobileWarning}: typeof CardItem = CardItem;
            confirm({
                className: "df-dialog-confirm",
                title: translate(`${translateMobileWarning}.title.text`),
                content: (
                    <>
                        <p>{translate(`${translateMobileWarning}.content.text`)}</p>
                        <p>{translate(`${translateMobileWarning}.question.text`)}</p>
                    </>
                ),
                okText: translate(`${translateMobileWarning}.continueBtn.text`),
                cancelText: translate(`${translateMobileWarning}.cancelBtn.text`),
                centered: true,
                onOk: this.confirmOpenHandler.bind(this, externalProject, url),
            });
        } else {
            this.confirmOpenHandler(externalProject, url);
        }
    }

    /**
     * Prevents propagation of click event
     * @param {React.MouseEvent} e
     */
    stopEventPropagation(e: React.MouseEvent): void {
        e.stopPropagation();
    }

    render(): React.ReactNode {
        const {
            data,
            phaseConfigList,
            activeJobType,
            activeTestType,
            isTest,
            translate,
        } = this.props;
        const {EXTERNAL}: typeof ProjectType = ProjectType;
        const {
            dueDate,
            title,
            projectType,
            name,
            assignee,
            description = "",
            decoratedStatus,
            jobCodename = "",
            testType,
            testSkill,
            phaseStats = {},
            taskCount = 0,
            completedTaskCount = 0,
        }: JobEntity = data;

        const currentPhaseConfig: PhaseConfig = phaseConfigList[`${projectType}-${name}`];
        const editor: string = isExternalProject(projectType) ? EXTERNAL.toLowerCase() : currentPhaseConfig?.editor;
        const prefix: string = getLocalePrefix(isTest);

        return (
            <div className="df-job-item">
                <div className={`df-${prefix}-upper`}>
                    {editor && <EditorIcon type={editor}/>}
                    <div className={`df-${prefix}-upper-container`}>
                        <div>
                            <JobTitle {...{translate, title, phaseName: name}} tooltipRequired tooltipPlacement="topLeft"/>
                            {!isTest ? (
                                <div className="df-job-status">
                                    <Translate id={`${CardItem.translationPath}.jobCodeNameLabel.text`}/>
                                    <span>{formatString(this.jobCodeNameFormat, jobCodename)}</span>
                                </div>
                            ) : (
                                <>
                                    <div>
                                        <span className="df-test-upper-label">
                                            <Translate id={`${CardItem.translationPath}.type.text`}/>
                                        </span>
                                        <span className="df-test-upper-type">
                                            {!!testType && <Translate id={`${CardItem.translationPath}.${testType}.text`}/>}
                                        </span>
                                    </div>
                                    <div>
                                        <span className="df-test-upper-label">
                                            <Translate id={`${CardItem.translationPath}.skill.text`}/>
                                        </span>
                                        <span className="df-test-upper-type">
                                            <Translate id={`${CardItem.translationPath}.${testSkill}.text`}/>
                                        </span>
                                    </div>
                                </>
                            )}
                        </div>
                        {(!isTest && assignee) && (
                            <div className="df-job-upper-info">
                                {activeJobType !== ContentType.COMPLETED ? (
                                    <>
                                        {dueDate && this.getRemainingTime(dueDate)}
                                        <ProgressBar
                                            phaseStats={phaseStats}
                                            taskCount={taskCount}
                                            completedTaskCount={completedTaskCount}
                                        />
                                    </>
                                ) : decoratedStatus && <JobStatus status={decoratedStatus}/>}
                            </div>
                        )}
                        {isTest && activeTestType === ContentType.COMPLETED && <TestScore data={data}/>}
                    </div>
                </div>
                <div className={isTest ? "df-test-lower" : "df-job-lower"}>
                    <ReactMarkdown linkTarget="_blank" className="df-job-desc">
                        {description}
                    </ReactMarkdown>
                    <div className="df-job-info-container">
                        <div className="df-job-stats-container">
                            {isTest ? this.getTestStats() : this.getRegularStats()}
                        </div>
                        <div className="df-job-action">
                            {this.actionsRenderer(data)}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

function mapStateToProps(state: StoreState): StateProps {
    return {
        translate: getTranslate(state.localize),
        workflowDefinitionList: state.job.workflowDefinitions,
        phaseConfigList: state.job.phaseConfigList,
        editors: state.serviceProperties.editors,
        isMobile: state.mobile.isMobile,
        activeJobType: state.job.activeJobType,
        activeTestType: state.test.activeTestType,
        playStoreUrl: state.serviceProperties.playStoreUrl,
        appStoreUrl: state.serviceProperties.appStoreUrl,
        isTest: state.job.isTest,
    };
}

const mapDispatchToProps: DispatchProps = {
    claimJobPhase,
    completeJobPhase,
    showConfirmClaimDialog,
    getJobPhaseWorkflowDefinitions,
    showDetailsDrawer,
    setSelectedJob,
    toggleInitiateWorkDialog,
    openExternalJob,
    setActiveJobType,
    setFilterType,
};

export default connect(mapStateToProps, mapDispatchToProps)(CardItem);
